import { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DeleteForeverRounded } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import SectionHeader from "shared/SectionHeader";
import LinearProgress from "components/LinearProgress";
import StyledMainButton from "components/buttons/StyledMainButton";
import { Message } from "components/SnackbarMessage";
import { MessageContext } from "utils/MessageProvider";
import { useFetch, useActionWithPath } from "utils/query/query";
import {
  GrantedProfile,
  PROFILES,
  UNRESTRICTED_PROFILE,
  VALIDITY_OPTIONS,
  describeStanding,
  formatAppliesTo,
  formatAssignedAt,
  formatValidUntil,
  isGrantableEmail,
  sortGrants,
  standingOf,
  standingSeverity,
} from "./profileGrants";

const LIST_PATH = "/user/profile/grantedProfiles";

/**
 * Who currently holds elevated access, and the controls to grant or remove it.
 *
 * Granted profiles are what let Orkes staff reach customer infrastructure: the support profiles
 * unlock the agent operations across the fleet, and ENGINEERING additionally carries globalBypass.
 * Until now they could be written but not read, so nobody could answer "who has support access
 * right now" without querying the database.
 *
 * The screen is ordered by what needs attention rather than by name: lapsed grants first, because
 * only revocation deactivates a row, so a grant whose validity ran out is still sitting in the
 * table granting nothing but also never cleaned up. Then permanent grants, which never lapse at
 * all. Ordinary active grants come last.
 */
const GrantedProfiles = () => {
  const { setMessage } = useContext(MessageContext);
  const [grantOpen, setGrantOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState<string>(PROFILES[0]);
  const [validitySeconds, setValiditySeconds] = useState<number>(
    VALIDITY_OPTIONS[1].seconds
  );
  const [pendingRemoval, setPendingRemoval] = useState<GrantedProfile | null>(
    null
  );

  const {
    data: grants = [],
    isFetching,
    refetch,
  } = useFetch<GrantedProfile[]>(LIST_PATH, {});

  const rows = useMemo(() => sortGrants(grants), [grants]);
  const lapsedCount = useMemo(
    () => rows.filter((g) => standingOf(g) === "lapsed").length,
    [rows]
  );

  const notify = (text: string, severity: "success" | "error") => {
    setMessage(new Message(text, severity));
    setTimeout(() => setMessage(null), 10000);
  };

  const grantAction = useActionWithPath({
    onSuccess: () => {
      setGrantOpen(false);
      setEmail("");
      notify("Profile granted", "success");
      refetch();
    },
    onError: () => notify("Could not grant the profile", "error"),
  });

  const removeAction = useActionWithPath({
    onSuccess: () => {
      setPendingRemoval(null);
      notify("Profile removed", "success");
      refetch();
    },
    onError: () => {
      setPendingRemoval(null);
      notify("Could not remove the profile", "error");
    },
  });

  const emailValid = isGrantableEmail(email);

  const grant = () => {
    // @ts-ignore mutate params are loosely typed across this codebase
    grantAction.mutate({
      method: "post",
      path: `/user/profile/addOrUpdateGrantedProfile?userEmail=${encodeURIComponent(
        email.trim()
      )}&userProfile=${profile}`,
      body: JSON.stringify({
        profileValidityInSeconds: validitySeconds,
        // The server writes "*" for all organizations, and narrowing it is not offered here:
        // a scoped grant needs a cluster picker, which belongs in its own change.
        profilesApplyTo: [{ org: "*", clusters: [] }],
      }),
    });
  };

  const remove = (row: GrantedProfile) => {
    // @ts-ignore mutate params are loosely typed across this codebase
    removeAction.mutate({
      method: "post",
      path: `/user/profile/removeGrantedProfile?userEmail=${encodeURIComponent(
        row.userEmail ?? ""
      )}&userProfile=${row.profile}`,
      body: "",
    });
  };

  return (
    <Box>
      <Helmet>
        <title>Granted Profiles</title>
      </Helmet>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <SectionHeader title="Granted Profiles" />
        <StyledMainButton onClick={() => setGrantOpen(true)}>
          Grant Profile
        </StyledMainButton>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Elevated access for Orkes staff. Support profiles unlock the agent
        operations across customer clusters; ENGINEERING additionally bypasses
        every organization check.
      </Typography>

      {lapsedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {lapsedCount === 1
            ? "1 grant has lapsed but was never removed."
            : `${lapsedCount} grants have lapsed but were never removed.`}{" "}
          A lapsed grant no longer confers access, but it stays on the record
          until it is removed.
        </Alert>
      )}

      {isFetching && <LinearProgress />}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Profile</TableCell>
              <TableCell>Standing</TableCell>
              <TableCell>Granted</TableCell>
              <TableCell>Valid until</TableCell>
              <TableCell>Applies to</TableCell>
              <TableCell>Granted by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !isFetching && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    Nobody currently holds an elevated profile.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const standing = standingOf(row);
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2">{row.userEmail}</Typography>
                    {row.userName && (
                      <Typography variant="caption" color="text.secondary">
                        {row.userName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.profile}
                      color={
                        row.profile === UNRESTRICTED_PROFILE
                          ? "error"
                          : "default"
                      }
                      variant={
                        row.profile === UNRESTRICTED_PROFILE
                          ? "filled"
                          : "outlined"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={describeStanding(standing)}
                      color={standingSeverity(standing)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatAssignedAt(row)}</TableCell>
                  <TableCell>{formatValidUntil(row)}</TableCell>
                  <TableCell>{formatAppliesTo(row)}</TableCell>
                  <TableCell>{row.grantedByEmail ?? "-"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove profile">
                      <span>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setPendingRemoval(row)}
                          aria-label={`Remove ${row.profile} from ${row.userEmail}`}
                        >
                          <DeleteForeverRounded fontSize="small" />
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Grant */}
      <Dialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Grant a profile</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Orkes email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={email.length > 0 && !emailValid}
              helperText={
                email.length > 0 && !emailValid
                  ? "Only @orkes.io addresses can hold a profile"
                  : " "
              }
              fullWidth
              size="small"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Profile</InputLabel>
              <Select
                label="Profile"
                value={profile}
                onChange={(e) => setProfile(String(e.target.value))}
              >
                {PROFILES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={profile === UNRESTRICTED_PROFILE}
            >
              <InputLabel>Valid for</InputLabel>
              <Select
                label="Valid for"
                value={validitySeconds}
                onChange={(e) => setValiditySeconds(Number(e.target.value))}
              >
                {VALIDITY_OPTIONS.map((option) => (
                  <MenuItem key={option.label} value={option.seconds}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {profile === UNRESTRICTED_PROFILE && (
              <Alert severity="warning">
                ENGINEERING bypasses every organization check and does not
                expire, whatever validity is recorded. It has to be removed by
                hand.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
          <StyledMainButton
            onClick={grant}
            disabled={!emailValid || grantAction.isLoading}
          >
            Grant
          </StyledMainButton>
        </DialogActions>
      </Dialog>

      {/* Remove */}
      <Dialog
        open={Boolean(pendingRemoval)}
        onClose={() => setPendingRemoval(null)}
      >
        <DialogTitle>Remove this profile?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingRemoval?.userEmail} will lose {pendingRemoval?.profile}{" "}
            immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeAction.isLoading}
            onClick={() => pendingRemoval && remove(pendingRemoval)}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GrantedProfiles;
